import { useEffect, useRef } from 'react'

/**
 * Возврат Mini App на передний план.
 *
 * Набор сигналов вынесен из useBuildVersion (#321) и повторно используется
 * блокирующим экраном «нет тарифа»: там человек уходит по ссылке регистрации в
 * браузер и возвращается — именно это событие нужно поймать, чтобы перепроверить
 * доступ.
 *
 * Почему сигналов четыре:
 *  - visibilitychange / focus — обычные веб-сигналы;
 *  - pageshow — единственный надёжный на мобильных: Telegram морозит WebView
 *    целиком (bfcache), таймеры стоят, а visibilitychange/focus на iOS могут не
 *    прийти вовсе;
 *  - Telegram Bot API 8.0+ `activated` — явное разворачивание; дополняет
 *    предыдущие, а не заменяет (на старых клиентах события нет).
 *
 * Колбэк держим в ref, чтобы слушатели не переподписывались на каждый рендер и
 * вызывающий мог передавать инлайновую замыкание.
 */
export function useAppForeground(onForeground: () => void, enabled = true): void {
  const cbRef = useRef(onForeground)
  cbRef.current = onForeground

  useEffect(() => {
    if (!enabled) return

    const fire = () => {
      if (document.visibilityState === 'visible') cbRef.current()
    }

    document.addEventListener('visibilitychange', fire)
    window.addEventListener('focus', fire)
    window.addEventListener('pageshow', fire)

    const tg = (window as unknown as {
      Telegram?: { WebApp?: { onEvent?: (e: string, cb: () => void) => void; offEvent?: (e: string, cb: () => void) => void } }
    }).Telegram?.WebApp
    const onActivated = () => cbRef.current()
    try {
      tg?.onEvent?.('activated', onActivated)
    } catch { /* событие не поддерживается — не страшно */ }

    return () => {
      document.removeEventListener('visibilitychange', fire)
      window.removeEventListener('focus', fire)
      window.removeEventListener('pageshow', fire)
      try {
        tg?.offEvent?.('activated', onActivated)
      } catch { /* см. выше */ }
    }
  }, [enabled])
}
